import cn from 'classnames';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAnalytics, getDashboardAdditionalData } from '../../../store/actions/analytics';
import { ANALYTICS_CAN_LOAD_REPORT_DATA, ANALYTICS_LOADING } from '../../../store/reducers/analytics';
import { useSticky } from '../../../hooks/useSticky';

import { CancellationsTable } from '../../tables/reports/CancellationsTable';
import { ReportsFilters } from './ReportsFilters';
import { SidebarPageLayout } from '../../layouts/SidebarPageLayout';
import { ReportsOrdersIcon } from '../../icons/analytics';
import { useAnalyticsMenuItems } from '../../../hooks/useAnalyticsMenuItems';

import { IncludeOnlyType } from '../../graphs/consts';
import { BY_DATE_TYPE_ORDER_CREATED, REPORT_FILTERS, REPORT_TYPES } from '../../tables/reports/consts';

import styles from './ReportsPage.module.scss';
import stickyStyles from '../../../stylesheets/scss/sticky.module.scss';

export function Cancellations() {
  const dispatch = useDispatch();
  const [sticky, ref] = useSticky();
  const analytics = useSelector((state) => state.analytics);
  const { sidebarMenuItems } = useAnalyticsMenuItems();

  const {
    filters,
    ui: {
      loading: { resetPagination },
    },
  } = analytics;

  const {
    table: { page, rowsPerPage },
    sort: { column, direction },
    canLoadData,
  } = analytics.reports[REPORT_TYPES.CANCELLATIONS];

  useEffect(() => {
    dispatch(getDashboardAdditionalData(REPORT_TYPES.CANCELLATIONS));
    // eslint-disable-next-line
  }, [filters.dates]);

  useEffect(() => {
    if (!canLoadData) return;

    dispatch(
      getAnalytics({
        kpi: REPORT_TYPES.CANCELLATIONS,
        additionalParams: {
          include_only: IncludeOnlyType.CANCELLED,
          date_type: BY_DATE_TYPE_ORDER_CREATED,
        },
        omitFilters: [REPORT_FILTERS.BY_DATE_TYPE, REPORT_FILTERS.WAREHOUSES],
        resetPagination: page > 0,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.retailerIds, filters.countries, filters.orderTypes, column, direction, rowsPerPage, canLoadData]);

  useEffect(() => {
    if (!canLoadData) return;

    if (resetPagination) {
      dispatch({ type: ANALYTICS_LOADING, payload: { resetPagination: false } });
    } else {
      dispatch(
        getAnalytics({
          kpi: REPORT_TYPES.CANCELLATIONS,
          additionalParams: {
            include_only: IncludeOnlyType.CANCELLED,
            date_type: BY_DATE_TYPE_ORDER_CREATED,
          },
          omitFilters: [REPORT_FILTERS.BY_DATE_TYPE, REPORT_FILTERS.WAREHOUSES],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    return () => {
      dispatch({
        type: ANALYTICS_CAN_LOAD_REPORT_DATA,
        payload: { reportType: REPORT_TYPES.CANCELLATIONS, canLoadData: false },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SidebarPageLayout items={sidebarMenuItems} hasStickyHeader>
      <div className={styles.reportWrapper}>
        <div className={stickyStyles.headerStickyObserver} ref={ref} />
        <header className={cn(styles.reportFilters, sticky ? stickyStyles.headerSticky : '')}>
          <h1>
            <ReportsOrdersIcon />
            Cancellations Report
          </h1>
          <ReportsFilters
            shownFilters={[
              REPORT_FILTERS.COUNTRIES,
              REPORT_FILTERS.SALES_CHANNELS,
              REPORT_FILTERS.DATE_RANGE,
              REPORT_FILTERS.CURRENCIES,
            ]}
            reportType={REPORT_TYPES.CANCELLATIONS}
          />
        </header>

        <main>
          <CancellationsTable />
        </main>
      </div>
    </SidebarPageLayout>
  );
}
